import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Alert,
  useMediaQuery, useTheme,
} from '@mui/material';
import { ClinicDialog } from '../components/dialogs/ClinicDialog';
import ConfirmDeleteDialog from '../components/dialogs/ConfirmDeleteDialog';
import { CLINIC_DIALOG_SX } from '../constants/popupStyles';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, LocalHospital as DiagnosisIcon } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';
import { useAuth } from '../context/AuthContext';
import diagnosisTypeService, { DiagnosisType, DiagnosisTypeCreate, DiagnosisTypeUpdate } from '../services/diagnosisTypeService';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import { diagnosisTypeColumns } from '../config/diagnosisTypeColumns';
import { formatDate } from '../utils/dateUtils';
import { useUserTimezone } from '../hooks/useUserTimezone';
import { useCrudHandlers } from '../hooks/useCrudHandlers';

// Reserved type seeded per tenant — the backend rejects renaming or deleting it.
const RESERVED_NAME = 'not_defined';

type FormData = DiagnosisTypeCreate & DiagnosisTypeUpdate;

const DiagnosisTypesPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const userTimezone = useUserTimezone();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<DiagnosisType | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>();

  const getSortValue = useCallback((item: DiagnosisType, key: string): any => {
    switch (key) {
      case 'name': return item.name.toLowerCase();
      case 'createdAt': return item.created_at;
      case 'updatedAt': return item.updated_at;
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((item: DiagnosisType): string[] => [item.name], []);

  const table = useTablePage<DiagnosisType>({
    entityType: 'diagnosis-types',
    columns: diagnosisTypeColumns,
    tenantId: currentUser?.tenant_id,
    getSortValue,
    getSearchText,
  });

  const crud = useCrudHandlers<DiagnosisType>({
    table,
    createFn: (data) => diagnosisTypeService.create(currentUser!.tenant_id!, data),
    updateFn: (id, data) => diagnosisTypeService.update(id as number, data),
    deleteFn: (id) => diagnosisTypeService.delete(id as number),
    entityName: 'Diagnosis Type',
    onSuccess: () => { setAddDialogOpen(false); setEditingItem(null); reset(); },
    onDeleteSuccess: () => { setDeleteDialogOpen(false); },
  });

  useEffect(() => {
    if (currentUser?.user_privilege?.wellness && currentUser.tenant_id != null) {
      table.loadData(() => diagnosisTypeService.getAll(currentUser.tenant_id!));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const openAdd = () => {
    setEditingItem(null);
    reset({ name: '' });
    setAddDialogOpen(true);
  };

  const openEdit = (item: DiagnosisType) => {
    if (item.name === RESERVED_NAME) return;
    setEditingItem(item);
    reset({ name: item.name });
    setAddDialogOpen(true);
  };

  const handleEdit = () => { if (table.selectedItem) openEdit(table.selectedItem); table.handleMenuClose(); };
  const handleDelete = () => { if (table.selectedItem) setDeleteDialogOpen(true); table.handleMenuClose(); };
  const onSubmit = (data: FormData) => {
    crud.handleCreateOrUpdate({ name: (data.name ?? '').trim() }, editingItem);
  };

  const getContextMenuOptions = (): ContextMenuOption[] => {
    // The reserved type can't be renamed or removed — keep the actions visible but inert.
    const isReserved = table.selectedItem?.name === RESERVED_NAME;
    return [
      { label: 'Edit', icon: <EditIcon sx={{ mr: 2 }} />, onClick: handleEdit, disabled: isReserved },
      { label: 'Delete', icon: <DeleteIcon sx={{ mr: 2 }} />, onClick: handleDelete, color: 'error', disabled: isReserved },
    ];
  };

  const getColumnValue = (item: DiagnosisType, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'name':
        return item.name === RESERVED_NAME ? (
          <Typography variant="body2" color="text.secondary">{item.name}</Typography>
        ) : (
          <Typography variant="body2" onClick={(e) => { e.stopPropagation(); openEdit(item); }}
            sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main', '&:hover': { color: 'primary.dark' } }}>
            {item.name}
          </Typography>
        );
      case 'createdAt': return formatDate(item.created_at, userTimezone);
      case 'updatedAt': return formatDate(item.updated_at, userTimezone);
      default: return 'N/A';
    }
  };

  const renderMobileCard = (item: DiagnosisType) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} sx={{ pr: 5 }}>
        <Typography variant="h6" sx={{ fontWeight: 'medium' }}>{item.name}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">{formatDate(item.created_at, userTimezone)}</Typography>
    </>
  );

  if (!currentUser?.user_privilege?.wellness) {
    return <Alert severity="error">You don't have permission to access this page.</Alert>;
  }

  return (
    <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading diagnosis types:">
      <DataTableToolbar
        {...table.toolbarProps}
        title="Diagnosis Types"
        searchPlaceholder="Search diagnosis types..."
        isAdmin={!!currentUser?.user_privilege?.config}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ whiteSpace: 'nowrap' }}>
            Add Diagnosis Type
          </Button>
        }
      />
      {isMobile ? (
        <MobileCardGrid data={table.sortedData} renderCard={renderMobileCard} onMenuClick={table.handleRowClick}
          getItemKey={(i) => i.id} emptyIcon={<DiagnosisIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
          emptyMessage="No diagnosis types found" />
      ) : (
        <DataTableContainer {...table.containerProps}>
          <DataTableBody data={table.sortedData} {...table.tableBodyProps} getColumnValue={getColumnValue}
            onRowClick={table.handleRowClick} getRowKey={(i) => i.id} />
        </DataTableContainer>
      )}

      <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

      <ClinicDialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}
        title={editingItem ? 'Edit Diagnosis Type' : 'Add Diagnosis Type'}
        maxWidth="sm"
        onSubmit={handleSubmit(onSubmit)}
        actions={
          <>
            <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
              {editingItem ? 'Update' : 'Create'}
            </Button>
            <Button onClick={() => setAddDialogOpen(false)} sx={CLINIC_DIALOG_SX.cancelButton}>Cancel</Button>
          </>
        }>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Name</Typography>
            <TextField {...register('name', { required: 'Name is required' })} fullWidth size="small"
              error={!!errors.name} helperText={errors.name?.message as string}
              InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
          </Box>
        </Box>
      </ClinicDialog>

      <ConfirmDeleteDialog open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); table.handleMenuClose(); }}
        onConfirm={() => crud.handleDelete(table.selectedItem)}
        entityType="Diagnosis Type" entityName={table.selectedItem?.name} />

      <ManageColumnsModal {...table.manageColumnsProps} />
      <SaveViewModal {...table.saveViewModalProps} isAdmin={!!currentUser?.user_privilege?.config} />
    </TablePageLayout>
  );
};

export default DiagnosisTypesPage;
